import {
  PreScreeningQuestion,
  QuestionType,
} from "../../types/landingPage";
import { mappers } from "../../utils/attributeMapping/mappers";
import {
  HORMONE_ATTRIBUTES,
  MENTAL_HEALTH_ATTRIBUTES,
  LIVER_KIDNEY_ATTRIBUTES,
  INFECTIOUS_DISEASE_ATTRIBUTES,
  CLINICAL_TRIAL_ATTRIBUTES,
  CANCER_ATTRIBUTES,
  OTHER_MEDICAL_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";

/**
 * Pre-screening questions for Mylan Contraceptive Patch Study
 */
export const mylanContraceptivePatchScreeningQuestions: PreScreeningQuestion[] = [
  {
    id: "age",
    type: QuestionType.RADIO,
    question: "What is your age?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "under_16",
        label: "Under 16 years old",
        disqualify: true,
      },
      {
        value: "16_to_35",
        label: "16 - 35 years old",
      },
      {
        value: "36_or_over",
        label: "36 years or older",
        disqualify: true,
      },
    ],
    disqualifyMessage: "This study is only available to participants between the ages of 16 and 35.",
  },

  {
    id: "biological_sex",
    type: QuestionType.RADIO,
    question: "What is your biological sex assigned at birth?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "female",
        label: "Female",
      },
      {
        value: "male",
        label: "Male",
        disqualify: true,
      },
      {
        value: "intersex",
        label: "Intersex",
        disqualify: true,
      },
    ],
    disqualifyMessage: "This study is only for individuals assigned female at birth, capable of falling pregnant.",
  },

  {
    id: "ethnicity",
    type: QuestionType.RADIO,
    question: "How would you describe your ethnicity or race?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "american_indian_alaska_native",
        label: "American Indian or Alaska Native",
        disqualify: true,
      },
      {
        value: "asian",
        label: "Asian",
        disqualify: true,
      },
      {
        value: "black_african_american",
        label: "Black or African American",
        disqualify: true,
      },
      {
        value: "native_hawaiian_pacific_islander",
        label: "Native Hawaiian or Pacific Islander",
        disqualify: true,
      },
      {
        value: "white",
        label: "White",
      },
      {
        value: "middle_eastern_north_african",
        label: "Middle Eastern or North African",
        disqualify: true,
      },
      {
        value: "prefer_not_answer",
        label: "I'd prefer not to answer",
        disqualify: true,
      },
      {
        value: "other",
        label: "Other",
        disqualify: true,
      },
    ],
    disqualifyMessage: "This study has finished enrolling participants in all ethnicity groups except White/Caucasian at this time.",
    attributeMapping: mappers.value({
      american_indian_alaska_native: [
        { attributeDefinitionId: 22520, radioAttributeValue: "000-6ls1x" },
      ],
      asian: [
        { attributeDefinitionId: 22520, radioAttributeValue: "001-xcv8ve" },
      ],
      black_african_american: [
        { attributeDefinitionId: 22520, radioAttributeValue: "002-uumkxi" },
      ],
      native_hawaiian_pacific_islander: [
        { attributeDefinitionId: 22520, radioAttributeValue: "003-lnrhpu" },
      ],
      white: [
        { attributeDefinitionId: 22520, radioAttributeValue: "004-1addnv" },
      ],
      middle_eastern_north_african: [
        { attributeDefinitionId: 22520, radioAttributeValue: "007-ka1bc" },
      ],
      prefer_not_answer: [
        { attributeDefinitionId: 22520, radioAttributeValue: "006-6i6gur" },
      ],
      other: [
        { attributeDefinitionId: 22520, radioAttributeValue: "005-1o2epp" },
      ],
    }),
  },

  {
    id: "reproductive_status",
    type: QuestionType.RADIO,
    question: "Which of the following best describes your reproductive status?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "pre_menarche",
        label: "I have not started having menstrual cycles (periods) yet",
        disqualify: true,
      },
      {
        value: "regular_cycles",
        label: "I have regular menstrual cycles (between 21 and 35 days apart over the past 6 months)",
      },
      {
        value: "hormonal_contraception",
        label: "I use hormonal contraception which may affect the timing of my menstrual cycles",
      },
      {
        value: "irregular_cycles",
        label: "I have irregular menstrual cycles (less than 21 days or more than 35 days apart over the past 6 months)",
        disqualify: true,
      },
      {
        value: "post_menopause",
        label: "I am post-menopausal",
        disqualify: true,
      },
    ],
    disqualifyMessage: "This study is only for women with regular menstrual cycles.",
    attributeMapping: mappers.value({
      pre_menarche: [
        { attributeDefinitionId: HORMONE_ATTRIBUTES.CAN_GET_PREGNANT, booleanAttributeValue: false },
      ],
      regular_cycles: [
        { attributeDefinitionId: HORMONE_ATTRIBUTES.CAN_GET_PREGNANT, booleanAttributeValue: true },
      ],
      hormonal_contraception: [
        { attributeDefinitionId: HORMONE_ATTRIBUTES.CAN_GET_PREGNANT, booleanAttributeValue: true },
        { attributeDefinitionId: HORMONE_ATTRIBUTES.ORAL_CONTRACEPTIVE, booleanAttributeValue: true },
      ],
      irregular_cycles: [
        { attributeDefinitionId: HORMONE_ATTRIBUTES.CAN_GET_PREGNANT, booleanAttributeValue: true },
      ],
      post_menopause: [
        { attributeDefinitionId: HORMONE_ATTRIBUTES.HAS_MENOPAUSE, booleanAttributeValue: true },
      ],
    }),
  },

  {
    id: "pregnancy_status",
    type: QuestionType.RADIO,
    question: "Are you currently pregnant or planning to become pregnant in the next 12 months?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "no",
        label: "No, I am not pregnant and do not plan to become pregnant in the next 12 months",
      },
      {
        value: "pregnant",
        label: "Yes, I am currently pregnant",
        disqualify: true,
      },
      {
        value: "planning_pregnancy",
        label: "Yes, I am planning to become pregnant in the next 12 months",
        disqualify: true,
      },
    ],
    disqualifyMessage: "This study is not suitable for those who are pregnant or planning to become pregnant.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_PREGNANT, booleanAttributeValue: false },
      ],
      pregnant: [
        { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_PREGNANT, booleanAttributeValue: true },
      ],
      planning_pregnancy: [], // No specific attribute for planning pregnancy
    }),
  },

  {
    id: "sexual_activity",
    type: QuestionType.RADIO,
    question: "Are you heterosexually active, engaging in vaginal intercourse at least once per menstrual cycle?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I engage in vaginal intercourse at least once per menstrual cycle",
      },
      {
        value: "no",
        label: "No, I do not engage in vaginal intercourse at least once per menstrual cycle",
        disqualify: true,
      },
    ],
    disqualifyMessage: "This study requires participants who are heterosexually active with vaginal intercourse at least once per menstrual cycle.",
  },

  {
    id: "fertility_status",
    type: QuestionType.RADIO,
    question: "As far as you know, are you and your partner(s) physically capable of conceiving a pregnancy together?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, as far as I know I am physically capable of conceiving with my partner(s)",
      },
      {
        value: "no",
        label: "No, I am not physically able to conceive with my partner(s) due to infertility or permanent sterilization (such as vasectomy or tubal ligation)",
        disqualify: true,
      },
    ],
    disqualifyMessage: "This study is not suitable for those with known infertility or permanent sterilization.",
  },

  {
    id: "contraceptive_intention",
    type: QuestionType.RADIO,
    question: "Are you planning to use hormonal contraception as your primary method for at least the next year?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I am planning to use hormonal contraception as my primary method for at least the next year",
      },
      {
        value: "no",
        label: "No, I do not intend to use hormonal contraception as my primary method for the next year",
        disqualify: true,
      },
    ],
    disqualifyMessage: "This study requires participants who intend to use hormonal contraception as their primary method for at least one year.",
  },


  {
    id: "recent_contraceptive_use",
    type: QuestionType.RADIO,
    question: "Have you used any of the following contraceptive methods recently?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "injectable_recent",
        label: "I have used an injectable contraceptive within the past 10 months (such as Depo-Provera or Depo-SubQ Provera)",
        disqualify: true,
      },
      {
        value: "implant_current",
        label: "I have a hormonal implant that is still in place (such as Nexplanon or Implanon)",
        disqualify: true,
      },
      {
        value: "implant_recent",
        label: "I have had a hormonal implant removed with less than 3 menstrual cycles since removal",
        disqualify: true,
      },
      {
        value: "iud_current",
        label: "I have an IUD that is still in place (such as Mirena or copper IUD)",
        disqualify: true,
      },
      {
        value: "iud_recent",
        label: "I had an IUD removed but my menstrual cycles have not yet returned",
        disqualify: true,
      },
      {
        value: "none",
        label: "None of the above is true for me",
      },
    ],
    disqualifyMessage: "This study requires a washout period from certain contraceptive methods.",
    attributeMapping: mappers.value({
      none: [], // No recent contraceptive use
      injectable_recent: [], // No specific attribute for injectable timing
      implant_current: [
        { attributeDefinitionId: HORMONE_ATTRIBUTES.IMPLANT, booleanAttributeValue: true },
      ],
      implant_recent: [
        { attributeDefinitionId: HORMONE_ATTRIBUTES.IMPLANT, booleanAttributeValue: true },
      ],
      iud_current: [
        { attributeDefinitionId: HORMONE_ATTRIBUTES.IUD, booleanAttributeValue: true },
      ],
      iud_recent: [
        { attributeDefinitionId: HORMONE_ATTRIBUTES.IUD, booleanAttributeValue: true },
      ],
    }),
  },

  {
    id: "recent_pregnancy_events",
    type: QuestionType.RADIO,
    question: "Have you had an abortion, miscarriage, ectopic pregnancy, vaginal delivery or Caesarean delivery in the past year?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes_with_periods",
        label: "Yes I have experienced one of those events and I have had at least 3 normal periods since",
      },
      {
        value: "yes_without_periods",
        label: "Yes I have experienced one of those events but I have NOT had at least 3 normal periods since",
        disqualify: true,
      },
      {
        value: "no",
        label: "Neither of those options is true for me",
      },
    ],
    disqualifyMessage: "This study requires at least 3 consecutive spontaneous menstrual cycles after pregnancy events.",
  },

  {
    id: "lactation_status",
    type: QuestionType.RADIO,
    question: "Are you currently breastfeeding or have you stopped breastfeeding recently?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "no",
        label: "No I am not currently breastfeeding",
      },
      {
        value: "currently_breastfeeding",
        label: "Yes, I am currently breastfeeding",
        disqualify: true,
      },
      {
        value: "recent_breastfeeding",
        label: "I stopped breastfeeding recently but have had less than 3 menstrual cycles since stopping",
        disqualify: true,
      },
    ],
    disqualifyMessage: "This study requires participants who are not breastfeeding and have had at least 3 menstrual cycles since stopping breastfeeding.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_BREASTFEEDING, booleanAttributeValue: false },
      ],
      currently_breastfeeding: [
        { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_BREASTFEEDING, booleanAttributeValue: true },
      ],
      recent_breastfeeding: [
        { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_BREASTFEEDING, booleanAttributeValue: true },
      ],
    }),
  },

  {
    id: "patch_sensitivity",
    type: QuestionType.RADIO,
    question: "Do you have any known skin sensitivity to patches or topical products (like plasters or surgical tape)?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "no",
        label: "No, my skin is not sensitive to patches or topical products",
      },
      {
        value: "yes",
        label: "Yes, my skin is sensitive to patches or topical products",
        disqualify: true,
      },
    ],
    disqualifyMessage: "This study involves a patch product and is not suitable for those with known dermal sensitivity.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: OTHER_MEDICAL_ATTRIBUTES.HAS_ALLERGIC_REACTION, booleanAttributeValue: false },
      ],
      yes: [
        { attributeDefinitionId: OTHER_MEDICAL_ATTRIBUTES.HAS_ALLERGIC_REACTION, booleanAttributeValue: true },
      ],
    }),
  },

  {
    id: "contraindications",
    type: QuestionType.RADIO,
    question: "Do any of the following apply to you?",
    helpText: "These are contraindications for progestin-only contraceptives",
    required: true,
    allowBack: true,
    options: [
      {
        value: "heart_disease",
        label: "I have had a heart attack or a stroke",
        disqualify: true,
      },
      {
        value: "lupus",
        label: "I have lupus with antiphospholipid antibodies",
        disqualify: true,
      },
      {
        value: "breast_cancer",
        label: "I have had breast cancer",
        disqualify: true,
      },
      {
        value: "liver_disease",
        label: "I have been diagnosed with liver disease or liver tumors (benign or malignant)",
        disqualify: true,
      },
      {
        value: "none",
        label: "None of the above is true for me",
      },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include people with these medical conditions.",
    attributeMapping: mappers.value({
      none: [], // No contraindications
      heart_disease: [
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: true },
      ],
      lupus: [
        { attributeDefinitionId: OTHER_MEDICAL_ATTRIBUTES.HAS_LUPUS, booleanAttributeValue: true },
      ],
      breast_cancer: [
        { attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: true },
      ],
      liver_disease: [
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: true },
      ],
    }),
  },

  {
    id: "uncontrolled_conditions",
    type: QuestionType.RADIO,
    question: "Do you have any of the following medical conditions?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "hereditary_angioedema",
        label: "Hereditary angioedema",
        disqualify: true,
      },
      {
        value: "liver_abnormalities",
        label: "Significant liver function abnormalities",
        disqualify: true,
      },
      {
        value: "uterine_cancer",
        label: "Cancer of the uterus, fallopian tubes or cervix",
        disqualify: true,
      },
      {
        value: "hepatitis",
        label: "Hepatitis B or C",
        disqualify: true,
      },
      {
        value: "hiv",
        label: "HIV",
        disqualify: true,
      },
      {
        value: "severe_depression",
        label: "Severe depression requiring hospitalization within the past 12 months",
        disqualify: true,
      },
      {
        value: "none",
        label: "I do not have any of the above medical conditions",
      },
    ],
    disqualifyMessage: "This study cannot include participants with these medical conditions.",
    attributeMapping: mappers.value({
      none: [], // No uncontrolled conditions
      hereditary_angioedema: [], // No specific attribute for hereditary angioedema
      liver_abnormalities: [
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: true },
      ],
      uterine_cancer: [
        { attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: true },
      ],
      hepatitis: [
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_B, booleanAttributeValue: true },
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_C, booleanAttributeValue: true },
      ],
      hiv: [
        { attributeDefinitionId: INFECTIOUS_DISEASE_ATTRIBUTES.HAS_HIV, booleanAttributeValue: true },
      ],
      severe_depression: [
        { attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_DEPRESSION, booleanAttributeValue: true },
      ],
    }),
  },






  {
    id: "study_compliance",
    type: QuestionType.RADIO,
    question: "Are you willing and able to comply with study requirements?",
    helpText: "This includes electronic diary entries, weekly patch application, and clinic visits every few months for a year.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I am willing and able to comply with the study requirements",
      },
      {
        value: "no",
        label: "No, I am not willing/able to comply with the study requirements",
        disqualify: true,
      },
      {
        value: "discuss",
        label: "I would like to discuss this schedule further with the research team",
      },
    ],
    disqualifyMessage: "This study requires participants who are willing and able to comply with the study requirements.",
  },

  {
    id: "currentTrial",
    type: QuestionType.RADIO,
    question: "Are you currently participating in any other clinical trials?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "no",
        label: "No, I am not participating in any other clinical trials",
      },
      {
        value: "yes",
        label: "Yes, I am currently participating in another clinical trial",
        disqualify: true,
      },
    ],
    disqualifyMessage: "You can only participate in one clinical trial at a time.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false },
      ],
      yes: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: true },
      ],
    }),
  },
]; 